import type { CatalogNamesInUse } from "@app/commands/catalog/getBrotherFileNames";
import { NEW_CATALOG_NAME } from "@app/config/const";
import type { ClientCatalogProps } from "@core/SitePresenter/SitePresenter";
import { uniqueName } from "@core/utils/uniqueName";
import FetchService from "@core-ui/ApiServices/FetchService";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import type CatalogEditProps from "@ext/catalog/actions/propsEditor/model/CatalogEditProps";
import { useGesCloudAnalytics } from "@ext/enterprise-cloud/ui-logic/analytics/GesCloudAnalyticsProvider";
import t from "@ext/localization/locale/translate";
import { Syntax } from "@ext/markdown/core/edit/logic/Formatter/Formatters/typeFormats/model/Syntax";
import { useRouter } from "../../../logic/Api/useRouter";

const CreateCatalog = ({ trigger, className }: { trigger: JSX.Element; className?: string }) => {
	const router = useRouter();
	const apiUrlCreator = ApiUrlCreatorService.value;
	const analytics = useGesCloudAnalytics();

	const getTakenCatalogNames = async (): Promise<string[]> => {
		const res = await FetchService.fetch<CatalogNamesInUse>(apiUrlCreator.getCatalogBrotherFileNames());
		if (!res.ok) return;
		const { names, reserved } = await res.json();
		return [...names, ...reserved.map((r) => r.name)];
	};

	const createCatalog = async () => {
		const catalogNames = await getTakenCatalogNames();
		if (!catalogNames) return;

		const props: CatalogEditProps = {
			url: uniqueName(NEW_CATALOG_NAME, catalogNames),
			syntax: Syntax.xml,
		};

		const responsePromise = FetchService.fetch<ClientCatalogProps>(
			apiUrlCreator.createCatalog(),
			JSON.stringify(props),
			MimeTypes.json,
		);
		ModalToOpenService.setValue(ModalToOpen.Loading, { title: `${t("loading")}` });
		const response = await responsePromise;
		ModalToOpenService.resetValue();
		if (!response.ok) return;
		const newCatalogProps = await response.json();
		analytics?.track({ name: "catalog_created", props: { catalog: newCatalogProps.name } });
		void router.pushPath(`/${newCatalogProps.link.pathname}`);
	};

	return (
		<div className={className} onClick={createCatalog}>
			{trigger}
		</div>
	);
};

export default CreateCatalog;
